// Path: src/App.jsx
// CHANGE: Added CandleUpload import + route at /officer/candle-upload

import React, { useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { Box, Paper, Typography, Button, Stack, alpha } from '@mui/material';
import { ErrorOutline as ErrorOutlineIcon, Refresh as RefreshIcon, Home as HomeIcon } from '@mui/icons-material';

// Auth
import ProtectedRoute from './components/ProtectedRoute';

// Layouts
import AdminLayout from './components/AdminLayout';
import OfficerLayout from './components/OfficerLayout';

// Auth Pages
import Login from './pages/Login';
import AdminSignup from './pages/AdminSignup';
import OfficerSignup from './pages/OfficerSignup';

// Admin Pages
import Dashboard from './pages/Dashboard';
import ScriptManagement from './pages/ScriptManagement';
import StrategyManagement from './pages/StrategyManagement';
import SignalDashboard from './pages/SignalDashboard';
import UserActivity from './pages/Admin/UserActivity';

// Alert Pages
import AlertManagement from './pages/AlertManagement';
import LiveAlertMonitor from './pages/LiveAlertMonitor';
import TestSignalSender from './pages/TestSignalSender';

import TradingData from './pages/TradingData';
import CandleUpload from './pages/TradingData/CandleUpload';
import StrategyGenerator from './pages/TradingData/StrategyGenerator';
import CompositeGenerator from './pages/TradingData/CompositeGenerator';

// Strategy Pages
import StrategyBuilder from './components/StrategyBuilder/StrategyBuilder';
import StrategyList from './components/StrategyList/StrategyList';
import StrategyRegistration from './components/StrategyRegistration/StrategyRegistration';
import StrategyDetail from './pages/Officer/StrategyDetail';
import AlertDetail from './pages/Officer/AlertDetail';
import TradeHistory from './pages/TradeHistory'; 
import AllTradesHistory from './pages/AllTradesHistory'; 

// Trading Components
import AutoSchedulerTabEnhanced from './components/Trading/AutoSchedulerTabEnhanced';
import LiveAlertsTabEnhanced from './components/Trading/LiveAlertsTabEnhanced';
import MultiStrategyDashboard from './components/Trading/MultiStrategyDashboard';
import BulkStrategyMonitor from './components/Trading/BulkStrategyMonitor';
import GenBulkStrategyMonitor from './components/Trading/BulkStrategyMonitor/GenBulkStrategyMonitor';
import BulkStrategyManagement from './components/Trading/BulkStrategyManagement';
import TraderStrategyViewer from './components/Trading/BulkStrategyMonitor/components/TraderStrategyViewer';
import BulkStrategyConfigManager from './components/BulkStrategyConfigManager';

// Other Components
import StrategyDocsViewer from './components/StrategyDocsViewer';
import StrategiesHistoricalData from './components/StrategiesHistoricalData';
import SignalMonitorPage from './components/SignalMonitor/SignalMonitorPage';

// Officer Pages
import OfficerDashboard from './pages/Officer/OfficerDashboard';
import OfficerStrategies from './pages/Officer/OfficerStrategies';
import OfficerAlerts from './pages/Officer/OfficerAlerts';
import OfficerReports from './pages/Officer/OfficerReports';
import OfficerProfile from './pages/Officer/OfficerProfile';
import TradingViewWebhookMonitor from './pages/Officer/TradingViewWebhookMonitor';
import NQDashboard from './components/TradingViewSignalMonitor/NQDashboard';
import SignalTestMonitor from './components/SignalTestMonitor';

// Global Components
import GlobalAlertNotification from './components/GlobalAlertNotification';
import GlobalStrategyNotifications from './components/GlobalStrategyNotifications';

import SimpleTradesHistory from './pages/SimpleTradesHistory';

// Signal Health Check
import SignalHealthCheck from './pages/SignalHealthCheck';

// Test Signal Launcher
import TestSignalLauncher from './pages/TestSignalLauncher';

// Agent Chat
import AgentChat from './pages/Officer/AgentChat';

// PineScript Hub
import PineScriptHub from './pages/Officer/PineScriptHub';

// Signal Nullify
import SignalNullify from './pages/Officer/SignalNullify';

// Candle Downloader
import CandleDownloader from './pages/Officer/CandleDownloader';

// Privacy
import Privacy from './pages/Privacy';

// Error Pages
import NotFound from './pages/NotFound';

// ============================================================================
// GLOBAL ERROR BOUNDARY
// ============================================================================
class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[APP-CRASH] 💥 Unhandled render error:', error);
    console.error('[APP-CRASH] Component stack:', errorInfo?.componentStack);
    this.setState({ errorInfo });
  }

  handleRecover = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  handleHardReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.hash = '';
    window.history.pushState({}, '', '/officer/bulk-strategies');
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message || 'Unknown error';
      const isChunkError = errorMessage.includes('Loading chunk') || 
                           errorMessage.includes('ChunkLoadError') ||
                           errorMessage.includes('Failed to fetch dynamically imported');

      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: '#0f1419', p: 3 }}>
          <Paper elevation={0} sx={{ p: 4, maxWidth: 520, textAlign: 'center', bgcolor: alpha('#1a1f2e', 0.95), border: `1px solid ${alpha('#ff4757', 0.3)}`, borderRadius: 2 }}>
            <ErrorOutlineIcon sx={{ fontSize: 72, color: '#ff4757', mb: 2 }} />
            <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
              {isChunkError ? 'Update Available' : 'Something Went Wrong'}
            </Typography>
            <Typography variant="body1" sx={{ color: alpha('#fff', 0.6), mb: 3 }}>
              {isChunkError 
                ? 'A new version of TradeWave is available. Please reload to update.'
                : 'The application encountered an unexpected error. You can try recovering or reload the page.'}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: alpha('#ff4757', 0.7), mb: 3, fontFamily: 'monospace', p: 1.5, bgcolor: alpha('#000', 0.3), borderRadius: 1, wordBreak: 'break-word', maxHeight: 80, overflow: 'auto' }}>
              {errorMessage}
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
              {!isChunkError && (
                <Button variant="contained" startIcon={<RefreshIcon />} onClick={this.handleRecover} sx={{ bgcolor: '#764ba2', fontWeight: 600, '&:hover': { bgcolor: '#5a3a7e' } }}>
                  Recover
                </Button>
              )}
              <Button variant={isChunkError ? 'contained' : 'outlined'} startIcon={<RefreshIcon />} onClick={this.handleHardReload}
                sx={isChunkError ? { bgcolor: '#764ba2', fontWeight: 600, '&:hover': { bgcolor: '#5a3a7e' } } : { borderColor: alpha('#00d4ff', 0.5), color: '#00d4ff', fontWeight: 600, '&:hover': { borderColor: '#00d4ff', bgcolor: alpha('#00d4ff', 0.1) } }}>
                {isChunkError ? 'Reload App' : 'Hard Reload'}
              </Button>
              {!isChunkError && (
                <Button variant="outlined" startIcon={<HomeIcon />} onClick={this.handleGoHome} sx={{ borderColor: alpha('#00ff88', 0.5), color: '#00ff88', fontWeight: 600, '&:hover': { borderColor: '#00ff88', bgcolor: alpha('#00ff88', 0.1) } }}>
                  Go to Monitor
                </Button>
              )}
            </Stack>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

// ============================================================================
// CONDITIONAL WRAPPERS
// ============================================================================

function ConditionalGlobalAlertNotification() {
  const location = useLocation();
  const disabledRoutes = ['/officer/gen-bulk-strategies'];
  const shouldShowAlerts = useMemo(() => {
    return !disabledRoutes.some(route => location.pathname.startsWith(route));
  }, [location.pathname]);
  if (!shouldShowAlerts) return null;
  return <GlobalAlertNotification />;
}

function ConditionalGlobalStrategyNotifications({ apiBaseUrl }) {
  const location = useLocation();
  const disabledRoutes = ['/login', '/officer/gen-bulk-strategies'];
  const shouldShowNotifications = useMemo(() => {
    return !disabledRoutes.some(route => location.pathname === route);
  }, [location.pathname]);
  if (!shouldShowNotifications) return null;
  return <GlobalStrategyNotifications apiBaseUrl={apiBaseUrl} />;
}

// ============================================================================
// APP
// ============================================================================
function App() {
  const API_BASE_URL = process.env.REACT_APP_API_URL || '/';

  return (
    <AuthProvider>
      <Router>
        <ConditionalGlobalStrategyNotifications apiBaseUrl={API_BASE_URL} />
        
        <AppErrorBoundary>
          <Routes>
            {/* PUBLIC ROUTES */}
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/admin-signup" element={<AdminSignup />} />
            <Route path="/privacy" element={<Privacy />} />

            {/* SHARED DASHBOARD */}
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />

            {/* ADMIN ROUTES */}
            <Route path="/admin/scripts" element={<ProtectedRoute requiredRole="admin"><AdminLayout><ScriptManagement /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/strategies" element={<ProtectedRoute requiredRole="admin"><AdminLayout><StrategyManagement /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/signals" element={<ProtectedRoute requiredRole="admin"><AdminLayout><SignalDashboard /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/alerts" element={<ProtectedRoute requiredRole="admin"><AdminLayout><AlertManagement /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/strategy-builder" element={<ProtectedRoute requiredRole="admin"><AdminLayout><StrategyList /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/strategy-builder/create" element={<ProtectedRoute requiredRole="admin"><AdminLayout><StrategyBuilder isEdit={false} /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/strategy-builder/edit/:strategyId" element={<ProtectedRoute requiredRole="admin"><AdminLayout><StrategyBuilder isEdit={true} /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/strategy-builder/:strategyId" element={<ProtectedRoute requiredRole="admin"><AdminLayout><StrategyDetail /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/user-activity" element={<ProtectedRoute requiredRole="admin"><AdminLayout><UserActivity /></AdminLayout></ProtectedRoute>} />
            <Route path="/officer-signup" element={<ProtectedRoute requiredRole="admin"><AdminLayout><OfficerSignup /></AdminLayout></ProtectedRoute>} />

            {/* OFFICER ROUTES */}
            <Route path="/officer/signal-monitor" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><SignalMonitorPage /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/signal-health" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><SignalHealthCheck apiBaseUrl={API_BASE_URL} /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/signal-launcher" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><TestSignalLauncher /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/test-signals" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><SignalTestMonitor /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/alert-management" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><AlertManagement /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/strategy-registration" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><StrategyRegistration /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/dashboard" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><OfficerDashboard /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/webhook-monitor" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><TradingViewWebhookMonitor /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/nq-dashboard" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><NQDashboard apiBaseUrl={API_BASE_URL} /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/strategy-docs" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><StrategyDocsViewer apiBaseUrl={API_BASE_URL} /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/historical-data" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><StrategiesHistoricalData apiBaseUrl={API_BASE_URL} /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/strategies-dashboard" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><MultiStrategyDashboard apiBaseUrl={API_BASE_URL} /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/auto-scheduler" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><AutoSchedulerTabEnhanced apiBaseUrl={API_BASE_URL} /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/live-alerts" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><LiveAlertsTabEnhanced apiBaseUrl={API_BASE_URL} /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/bulk-strategies" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><BulkStrategyMonitor apiBaseUrl={API_BASE_URL} /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/gen-bulk-strategies" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><GenBulkStrategyMonitor apiBaseUrl={API_BASE_URL} /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/trade-history" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><TradeHistory apiBaseUrl={API_BASE_URL} /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/trades/all" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><AllTradesHistory apiBaseUrl={API_BASE_URL} /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/strategy-management" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><BulkStrategyManagement apiBaseUrl={API_BASE_URL} /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/strategy-config" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><BulkStrategyConfigManager apiBaseUrl={API_BASE_URL} /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/strategy-viewer/:strategyId" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><TraderStrategyViewer apiBaseUrl={API_BASE_URL} /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/strategies/:strategyId" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><StrategyDetail /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/strategies" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><OfficerStrategies /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/alerts" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><OfficerAlerts /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/reports" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><OfficerReports /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/profile" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><OfficerProfile /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/simple-trades" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><SimpleTradesHistory apiBaseUrl={API_BASE_URL} /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/trading-data" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><TradingData /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/strategy-generator" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><StrategyGenerator /></OfficerLayout></ProtectedRoute>} />
            <Route path="/officer/composite-generator" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><CompositeGenerator /></OfficerLayout></ProtectedRoute>} />

            {/* PINESCRIPT HUB */}
            <Route path="/officer/pinescript-hub" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><PineScriptHub /></OfficerLayout></ProtectedRoute>} />

            {/* SIGNAL NULLIFY */}
            <Route path="/officer/signal-nullify" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><SignalNullify /></OfficerLayout></ProtectedRoute>} />

            {/* CANDLE DOWNLOADER */}
            <Route path="/officer/candle-downloader" element={<ProtectedRoute requiredRole="officer"><OfficerLayout><CandleDownloader /></OfficerLayout></ProtectedRoute>} />

            {/* CANDLE UPLOAD */}
            <Route
              path="/officer/candle-upload"
              element={
                <ProtectedRoute requiredRole="officer">
                  <OfficerLayout><CandleUpload /></OfficerLayout>
                </ProtectedRoute>
              }
            />

            {/* AGENT CHAT */}
            <Route
              path="/officer/agent-chat"
              element={
                <ProtectedRoute requiredRole="officer">
                  <OfficerLayout>
                    <Box sx={{ height: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
                      <AgentChat />
                    </Box>
                  </OfficerLayout>
                </ProtectedRoute>
              }
            />

            {/* ERROR ROUTES */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </AppErrorBoundary>
      </Router>
    </AuthProvider>
  );
}

export default App;









// // App.jsx
// import React, { useMemo } from 'react';
// import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
// import { AuthProvider } from './contexts/AuthContext';
// import { Box, Paper, Typography, Button, Stack, alpha } from '@mui/material';
// import { ErrorOutline as ErrorOutlineIcon, Refresh as RefreshIcon, Home as HomeIcon } from '@mui/icons-material';

// // Auth
// import ProtectedRoute from './components/ProtectedRoute';

// // Layouts
// import AdminLayout from './components/AdminLayout';
// import OfficerLayout from './components/OfficerLayout';

// // Auth Pages
// import Login from './pages/Login';
// import AdminSignup from './pages/AdminSignup';
// import OfficerSignup from './pages/OfficerSignup';

// // Admin Pages
// import Dashboard from './pages/Dashboard';
// import ScriptManagement from './pages/ScriptManagement';
// import StrategyManagement from './pages/StrategyManagement';
// import SignalDashboard from './pages/SignalDashboard';
// import UserActivity from './pages/Admin/UserActivity';

// // Alert Pages
// import AlertManagement from './pages/AlertManagement';
// import LiveAlertMonitor from './pages/LiveAlertMonitor';
// import TestSignalSender from './pages/TestSignalSender';


// import TradingData from './pages/TradingData';


// // Strategy Pages
// import StrategyBuilder from './components/StrategyBuilder/StrategyBuilder';
// import StrategyList from './components/StrategyList/StrategyList';
// import StrategyRegistration from './components/StrategyRegistration/StrategyRegistration';
// import StrategyDetail from './pages/Officer/StrategyDetail';
// import AlertDetail from './pages/Officer/AlertDetail';
// import TradeHistory from './pages/TradeHistory'; 
// import AllTradesHistory from './pages/AllTradesHistory'; 

// // Trading Components
// import AutoSchedulerTabEnhanced from './components/Trading/AutoSchedulerTabEnhanced';
// import LiveAlertsTabEnhanced from './components/Trading/LiveAlertsTabEnhanced';
// import MultiStrategyDashboard from './components/Trading/MultiStrategyDashboard';
// import BulkStrategyMonitor from './components/Trading/BulkStrategyMonitor';
// import GenBulkStrategyMonitor from './components/Trading/BulkStrategyMonitor/GenBulkStrategyMonitor';
// import BulkStrategyManagement from './components/Trading/BulkStrategyManagement';
// import TraderStrategyViewer from './components/Trading/BulkStrategyMonitor/components/TraderStrategyViewer';
// import BulkStrategyConfigManager from './components/BulkStrategyConfigManager';

// // Other Components
// import StrategyDocsViewer from './components/StrategyDocsViewer';
// import StrategiesHistoricalData from './components/StrategiesHistoricalData';
// import SignalMonitorPage from './components/SignalMonitor/SignalMonitorPage';

// // Officer Pages
// import OfficerDashboard from './pages/Officer/OfficerDashboard';
// import OfficerStrategies from './pages/Officer/OfficerStrategies';
// import OfficerAlerts from './pages/Officer/OfficerAlerts';
// import OfficerReports from './pages/Officer/OfficerReports';
// import OfficerProfile from './pages/Officer/OfficerProfile';
// import TradingViewWebhookMonitor from './pages/Officer/TradingViewWebhookMonitor';
// import NQDashboard from './components/TradingViewSignalMonitor/NQDashboard';
// import SignalTestMonitor from './components/SignalTestMonitor';

// // Global Components
// import GlobalAlertNotification from './components/GlobalAlertNotification';
// import GlobalStrategyNotifications from './components/GlobalStrategyNotifications';

// import SimpleTradesHistory from './pages/SimpleTradesHistory';

// // Signal Health Check
// import SignalHealthCheck from './pages/SignalHealthCheck';

// // Test Signal Launcher
// import TestSignalLauncher from './pages/TestSignalLauncher';

// // Error Pages
// import NotFound from './pages/NotFound';

// // ============================================================================
// // GLOBAL ERROR BOUNDARY — catches crashes from ANY page/component
// // ============================================================================
// class AppErrorBoundary extends React.Component {
//   constructor(props) {
//     super(props);
//     this.state = { hasError: false, error: null, errorInfo: null };
//   }

//   static getDerivedStateFromError(error) {
//     return { hasError: true, error };
//   }

//   componentDidCatch(error, errorInfo) {
//     console.error('[APP-CRASH] 💥 Unhandled render error:', error);
//     console.error('[APP-CRASH] Component stack:', errorInfo?.componentStack);
//     this.setState({ errorInfo });
//   }

//   handleRecover = () => {
//     this.setState({ hasError: false, error: null, errorInfo: null });
//   };

//   handleHardReload = () => {
//     window.location.reload();
//   };

//   handleGoHome = () => {
//     this.setState({ hasError: false, error: null, errorInfo: null });
//     window.location.hash = '';
//     window.history.pushState({}, '', '/officer/bulk-strategies');
//     window.location.reload();
//   };

//   render() {
//     if (this.state.hasError) {
//       const errorMessage = this.state.error?.message || 'Unknown error';
//       const isChunkError = errorMessage.includes('Loading chunk') || 
//                            errorMessage.includes('ChunkLoadError') ||
//                            errorMessage.includes('Failed to fetch dynamically imported');

//       return (
//         <Box
//           sx={{
//             display: 'flex',
//             justifyContent: 'center',
//             alignItems: 'center',
//             minHeight: '100vh',
//             bgcolor: '#0f1419',
//             p: 3,
//           }}
//         >
//           <Paper
//             elevation={0}
//             sx={{
//               p: 4,
//               maxWidth: 520,
//               textAlign: 'center',
//               bgcolor: alpha('#1a1f2e', 0.95),
//               border: `1px solid ${alpha('#ff4757', 0.3)}`,
//               borderRadius: 2,
//             }}
//           >
//             <ErrorOutlineIcon sx={{ fontSize: 72, color: '#ff4757', mb: 2 }} />
//             <Typography variant="h4" sx={{ color: '#fff', fontWeight: 700, mb: 1 }}>
//               {isChunkError ? 'Update Available' : 'Something Went Wrong'}
//             </Typography>
//             <Typography variant="body1" sx={{ color: alpha('#fff', 0.6), mb: 3 }}>
//               {isChunkError 
//                 ? 'A new version of TradeWave is available. Please reload to update.'
//                 : 'The application encountered an unexpected error. You can try recovering or reload the page.'}
//             </Typography>
//             <Typography
//               variant="caption"
//               sx={{
//                 display: 'block',
//                 color: alpha('#ff4757', 0.7),
//                 mb: 3,
//                 fontFamily: 'monospace',
//                 p: 1.5,
//                 bgcolor: alpha('#000', 0.3),
//                 borderRadius: 1,
//                 wordBreak: 'break-word',
//                 maxHeight: 80,
//                 overflow: 'auto',
//               }}
//             >
//               {errorMessage}
//             </Typography>
//             <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
//               {!isChunkError && (
//                 <Button
//                   variant="contained"
//                   startIcon={<RefreshIcon />}
//                   onClick={this.handleRecover}
//                   sx={{
//                     bgcolor: '#764ba2',
//                     fontWeight: 600,
//                     '&:hover': { bgcolor: '#5a3a7e' },
//                   }}
//                 >
//                   Recover
//                 </Button>
//               )}
//               <Button
//                 variant={isChunkError ? 'contained' : 'outlined'}
//                 startIcon={<RefreshIcon />}
//                 onClick={this.handleHardReload}
//                 sx={isChunkError ? {
//                   bgcolor: '#764ba2',
//                   fontWeight: 600,
//                   '&:hover': { bgcolor: '#5a3a7e' },
//                 } : {
//                   borderColor: alpha('#00d4ff', 0.5),
//                   color: '#00d4ff',
//                   fontWeight: 600,
//                   '&:hover': {
//                     borderColor: '#00d4ff',
//                     bgcolor: alpha('#00d4ff', 0.1),
//                   },
//                 }}
//               >
//                 {isChunkError ? 'Reload App' : 'Hard Reload'}
//               </Button>
//               {!isChunkError && (
//                 <Button
//                   variant="outlined"
//                   startIcon={<HomeIcon />}
//                   onClick={this.handleGoHome}
//                   sx={{
//                     borderColor: alpha('#00ff88', 0.5),
//                     color: '#00ff88',
//                     fontWeight: 600,
//                     '&:hover': {
//                       borderColor: '#00ff88',
//                       bgcolor: alpha('#00ff88', 0.1),
//                     },
//                   }}
//                 >
//                   Go to Monitor
//                 </Button>
//               )}
//             </Stack>
//           </Paper>
//         </Box>
//       );
//     }

//     return this.props.children;
//   }
// }

// // ============================================================================
// // CONDITIONAL WRAPPERS
// // ============================================================================

// // Wrapper component to conditionally render GlobalAlertNotification
// function ConditionalGlobalAlertNotification() {
//   const location = useLocation();
  
//   const disabledRoutes = [
//     '/officer/gen-bulk-strategies'
//   ];
  
//   const shouldShowAlerts = useMemo(() => {
//     return !disabledRoutes.some(route => location.pathname.startsWith(route));
//   }, [location.pathname]);
  
//   if (!shouldShowAlerts) {
//     return null;
//   }
  
//   return <GlobalAlertNotification />;
// }

// // Wrapper to only show notifications when logged in (not on login page)
// function ConditionalGlobalStrategyNotifications({ apiBaseUrl }) {
//   const location = useLocation();
  
//   const disabledRoutes = [
//     '/login',
//     // '/officer/bulk-strategies',   // BulkStrategyMonitor handles its own notifications
//     '/officer/gen-bulk-strategies', // GenBulkStrategyMonitor handles its own notifications
//   ];
  
//   const shouldShowNotifications = useMemo(() => {
//     return !disabledRoutes.some(route => location.pathname === route);
//   }, [location.pathname]);
  
//   if (!shouldShowNotifications) {
//     return null;
//   }
  
//   return <GlobalStrategyNotifications apiBaseUrl={apiBaseUrl} />;
// }

// // ============================================================================
// // APP
// // ============================================================================
// function App() {
//   const API_BASE_URL = process.env.REACT_APP_API_URL || '/';

//   return (
//     <AuthProvider>
//       <Router>
//         {/* <ConditionalGlobalAlertNotification /> */}
        
//         {/* Global Strategy Notifications - runs on ALL pages (single source of truth) */}
//         <ConditionalGlobalStrategyNotifications apiBaseUrl={API_BASE_URL} />
        
//         {/* Global Error Boundary — catches crashes from ANY route */}
//         <AppErrorBoundary>
//           <Routes>
//             {/* PUBLIC ROUTES */}
//             <Route path="/" element={<Navigate to="/login" replace />} />
//             <Route path="/login" element={<Login />} />
//             <Route path="/admin-signup" element={<AdminSignup />} />

//             {/* SHARED DASHBOARD */}
//             <Route
//               path="/dashboard"
//               element={
//                 <ProtectedRoute>
//                   <Dashboard />
//                 </ProtectedRoute>
//               }
//             />

//             {/* ADMIN ROUTES */}
//             <Route
//               path="/admin/scripts"
//               element={
//                 <ProtectedRoute requiredRole="admin">
//                   <AdminLayout><ScriptManagement /></AdminLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/admin/strategies"
//               element={
//                 <ProtectedRoute requiredRole="admin">
//                   <AdminLayout><StrategyManagement /></AdminLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/admin/signals"
//               element={
//                 <ProtectedRoute requiredRole="admin">
//                   <AdminLayout><SignalDashboard /></AdminLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/admin/alerts"
//               element={
//                 <ProtectedRoute requiredRole="admin">
//                   <AdminLayout><AlertManagement /></AdminLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/admin/strategy-builder"
//               element={
//                 <ProtectedRoute requiredRole="admin">
//                   <AdminLayout><StrategyList /></AdminLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/admin/strategy-builder/create"
//               element={
//                 <ProtectedRoute requiredRole="admin">
//                   <AdminLayout><StrategyBuilder isEdit={false} /></AdminLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/admin/strategy-builder/edit/:strategyId"
//               element={
//                 <ProtectedRoute requiredRole="admin">
//                   <AdminLayout><StrategyBuilder isEdit={true} /></AdminLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/admin/strategy-builder/:strategyId"
//               element={
//                 <ProtectedRoute requiredRole="admin">
//                   <AdminLayout><StrategyDetail /></AdminLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/admin/user-activity"
//               element={
//                 <ProtectedRoute requiredRole="admin">
//                   <AdminLayout><UserActivity /></AdminLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer-signup"
//               element={
//                 <ProtectedRoute requiredRole="admin">
//                   <AdminLayout><OfficerSignup /></AdminLayout>
//                 </ProtectedRoute>
//               }
//             />

//             {/* OFFICER ROUTES */}
//             <Route
//               path="/officer/signal-monitor"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><SignalMonitorPage /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/signal-health"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><SignalHealthCheck apiBaseUrl={API_BASE_URL} /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/signal-launcher"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><TestSignalLauncher /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/test-signals"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><SignalTestMonitor /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/alert-management"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><AlertManagement /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/strategy-registration"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><StrategyRegistration /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/dashboard"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><OfficerDashboard /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/webhook-monitor"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><TradingViewWebhookMonitor /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/nq-dashboard"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout>
//                     <NQDashboard 
//                       apiBaseUrl={API_BASE_URL}
//                       wsUrl="wss://ojuimo.com/ws/nq-dashboard/"
//                     />
//                   </OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/strategy-docs"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><StrategyDocsViewer apiBaseUrl={API_BASE_URL} /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/historical-data"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><StrategiesHistoricalData apiBaseUrl={API_BASE_URL} /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/strategies-dashboard"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><MultiStrategyDashboard apiBaseUrl={API_BASE_URL} /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/auto-scheduler"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><AutoSchedulerTabEnhanced apiBaseUrl={API_BASE_URL} /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/live-alerts"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><LiveAlertsTabEnhanced apiBaseUrl={API_BASE_URL} /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/bulk-strategies"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><BulkStrategyMonitor apiBaseUrl={API_BASE_URL} /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/gen-bulk-strategies"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><GenBulkStrategyMonitor apiBaseUrl={API_BASE_URL} /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/trade-history"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><TradeHistory apiBaseUrl={API_BASE_URL} /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/trades/all"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><AllTradesHistory /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/strategy-management"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><BulkStrategyManagement apiBaseUrl={API_BASE_URL} /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/strategy-config"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><BulkStrategyConfigManager apiBaseUrl={API_BASE_URL} /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/strategy-viewer/:strategyId"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><TraderStrategyViewer apiBaseUrl={API_BASE_URL} /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/strategies/:strategyId"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><StrategyDetail /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/strategies"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><OfficerStrategies /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/alerts"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><OfficerAlerts /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/reports"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><OfficerReports /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/profile"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><OfficerProfile /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />
//             <Route
//               path="/officer/simple-trades"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><SimpleTradesHistory apiBaseUrl={API_BASE_URL} /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />

//             <Route
//               path="/officer/trading-data"
//               element={
//                 <ProtectedRoute requiredRole="officer">
//                   <OfficerLayout><TradingData /></OfficerLayout>
//                 </ProtectedRoute>
//               }
//             />

//             {/* ERROR ROUTES */}
//             <Route path="*" element={<NotFound />} />
//           </Routes>
//         </AppErrorBoundary>
//       </Router>
//     </AuthProvider>
//   );
// }

// export default App;
